import React, { createContext, useContext, useEffect, useState } from 'react';
import { courseModules as fallbackModules } from '../data/courseData';

/**
 * ModulesContext: single source of truth for module metadata.
 * Loads /api/modules once at app mount so titles, slugs and ordering stay
 * aligned with the backend. Falls back to the bundled metadata only while
 * the request is in flight.
 */
const ModulesContext = createContext();
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function ModulesProvider({ children }) {
  const [modules, setModules] = useState(fallbackModules);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${BACKEND}/api/modules`);
        if (resp.ok) {
          const data = await resp.json();
          if (!cancelled && Array.isArray(data.modules) && data.modules.length) {
            const normalised = data.modules.map((m) => ({
              id: m.id,
              slug: m.slug,
              title: m.title,
              shortTitle: m.shortTitle,
              icon: m.icon,
              description: m.description,
              estimatedTime: m.estimatedTime,
              isFree: !!m.is_free,
            }));
            setModules(normalised);
          }
        }
      } catch (e) {
        console.error('ModulesContext: fetch failed, using bundled fallback', e);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <ModulesContext.Provider value={{ modules, loaded }}>
      {children}
    </ModulesContext.Provider>
  );
}

export function useModules() {
  const ctx = useContext(ModulesContext);
  if (!ctx) throw new Error('useModules must be inside ModulesProvider');
  return ctx;
}
